import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Grid, Typography, alpha, Stack, Link } from '@mui/material';
import { motion } from 'framer-motion';
import { MailOutlined, CheckCircleFilled } from '@ant-design/icons';
import DocumentRequestForm from 'sections/landing/DocumentRequestForm';
import { LEAD_DOCUMENTS, LEAD_MAIL_TO } from 'const/documents';
import { PageUrl } from 'const/page-url';

// asset（配布PDF「OHANA AI サービス紹介資料」の 1・3・7 ページ目から生成したサムネイル。
// 資料を差し替えたら pdftoppm などで作り直すこと）
import docService1 from 'assets/images/doc-service-1.webp';
import docService2 from 'assets/images/doc-service-2.webp';
import docService3 from 'assets/images/doc-service-3.webp';

// ==============================|| DOCUMENT REQUEST TOP ||============================== //
//
// 資料請求専用ページ(/document-request)の本体。
// トップページには省スペースのバナーだけを置き、実際の入力はこのページで行う。

const primaryColor = '#2aa1af';
const accentRed = '#e2574c';

const thumbnails = [
  { src: docService3, alt: 'サービス紹介資料の誌面（個別支援計画書の自動作成）' },
  { src: docService2, alt: 'サービス紹介資料の誌面（OHANA AI ができること）' },
  { src: docService1, alt: 'サービス紹介資料の表紙' }
];

/** 「資料の内容」に並べる目次。資料を差し替えたら見出しにあわせて直すこと */
const contents = [
  'AIロボによるアセスメントと診断結果の提示',
  '個別支援計画書・専門的支援実施計画書の自動作成',
  '専門的支援加算の増収シミュレーション',
  '減算対策と、加算の算定に必要な記録の作成',
  '料金・導入までの流れ'
];

const DocumentRequestTop = () => (
  <Box
    component="section"
    sx={{
      py: { xs: 8, md: 12 },
      background: `linear-gradient(180deg, ${alpha(primaryColor, 0.08)} 0%, ${alpha(primaryColor, 0.04)} 100%)`
    }}
  >
    <Container maxWidth="lg">
      <Grid container spacing={{ xs: 6, md: 5 }} alignItems="center">
        {/* ============ 左：資料の紹介 ============ */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Box sx={{ textAlign: 'center' }}>
              {/* このページ唯一の見出し。h2 にすると h1 不在のページになるため h1 で出す
                  (variant は見た目のサイズなので h2 のまま) */}
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '1.6rem', md: '2rem' },
                  color: '#1a1a1a',
                  lineHeight: 1.45,
                  mb: 2.5
                }}
              >
                OHANA AI のことが
                <Box component="span" sx={{ color: accentRed }}>3分</Box>
                でわかる！
                <br />
                サービス紹介資料を受け取る
              </Typography>

              <Typography
                component="p"
                sx={{ color: '#4b5563', fontSize: { xs: '0.92rem', md: '1rem' }, lineHeight: 1.9, fontWeight: 600, mb: 4 }}
              >
                AIによるアセスメントから個別支援計画書の自動作成、
                <br />
                加算の増収シミュレーションと減算対策まで1冊で！
                <Box component="span" sx={{ display: 'block', color: primaryColor, fontWeight: 800, mt: 1.5 }}>
                  1ヶ月の無料トライアルもご案内しています
                </Box>
              </Typography>

              {/* 誌面のサムネイル（横長スライドを斜めに重ねて束ねて見せる） */}
              <Box
                sx={{
                  position: 'relative',
                  height: { xs: 210, md: 260 },
                  mb: 4,
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                {thumbnails.map((thumb, index) => (
                  <Box
                    key={thumb.src}
                    component="img"
                    src={thumb.src}
                    alt={thumb.alt}
                    loading="lazy"
                    sx={{
                      position: 'absolute',
                      // 手前（配列の最後＝表紙）ほど下・右に来るようにずらす
                      bottom: `${(thumbnails.length - 1 - index) * 26}px`,
                      left: `calc(50% - ${(thumbnails.length - 1 - index) * 16}px)`,
                      transform: 'translateX(-50%)',
                      width: { xs: 270, md: 340 },
                      height: 'auto',
                      borderRadius: '6px',
                      bgcolor: '#fff',
                      border: '1px solid #e0e6e6',
                      boxShadow: `0 12px 28px ${alpha('#000', 0.14)}`,
                      zIndex: index + 1
                    }}
                  />
                ))}
              </Box>

              {/* 資料の内容 */}
              <Box
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: '14px',
                  bgcolor: '#fff',
                  border: '1px solid #e5eaea',
                  textAlign: 'left',
                  mb: 3
                }}
              >
                <Typography
                  component="p"
                  sx={{ fontWeight: 900, fontSize: '0.95rem', color: '#1a1a1a', mb: 0.4 }}
                >
                  {LEAD_DOCUMENTS[0].title}
                  <Box component="span" sx={{ color: '#9aa5a5', fontWeight: 600, fontSize: '0.76rem', ml: 0.8 }}>
                    {LEAD_DOCUMENTS[0].volume}
                  </Box>
                </Typography>
                <Typography component="p" sx={{ color: '#8a9393', fontSize: '0.8rem', fontWeight: 700, mb: 1.5 }}>
                  資料の内容
                </Typography>
                <Stack spacing={1} component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
                  {contents.map((item) => (
                    <Stack key={item} direction="row" spacing={1.2} component="li" alignItems="flex-start">
                      <CheckCircleFilled
                        aria-hidden="true"
                        style={{ color: primaryColor, fontSize: '0.85rem', marginTop: '5px', flexShrink: 0 }}
                      />
                      <Typography component="span" sx={{ color: '#4b5563', fontSize: '0.88rem', lineHeight: 1.7, fontWeight: 700 }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              {/* メールでの問い合わせ（電話受付は行っていないため、メールのみを案内する） */}
              <Box
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: '14px',
                  border: `1px solid ${alpha(primaryColor, 0.3)}`
                }}
              >
                <Typography component="p" sx={{ color: '#4b5563', fontSize: '0.88rem', fontWeight: 700, mb: 1.2 }}>
                  メールでもお問い合わせいただけます
                </Typography>
                <Link
                  href={`mailto:${LEAD_MAIL_TO}?subject=${encodeURIComponent('OHANA AI へのお問い合わせ')}`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    color: primaryColor,
                    fontWeight: 900,
                    fontSize: { xs: '1rem', md: '1.2rem' },
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  <MailOutlined aria-hidden="true" />
                  {LEAD_MAIL_TO}
                </Link>
                <Typography component="p" sx={{ color: '#9aa5a5', fontSize: '0.78rem', mt: 1.2, lineHeight: 1.7 }}>
                  お問い合わせの受付は電子メールのみとなります。詳しくは
                  <Link
                    component={RouterLink}
                    to={PageUrl.PrivacyPolicy.urlWithSlash}
                    sx={{ color: primaryColor, fontWeight: 700, mx: 0.4 }}
                  >
                    プライバシーポリシー
                  </Link>
                  をご覧ください。
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </Grid>

        {/* ============ 右：資料請求フォーム ============ */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <DocumentRequestForm heading="カンタン30秒で完了！" />
          </motion.div>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default DocumentRequestTop;
